import { AnalyticsService, Flavor, IRecommendationArgs, recommendation } from "@gtmhub/core/analytics";
import { IIndicator } from "@gtmhub/error-handling";
import { IInsightboard, IInsightTemplateSuggestionSetting } from "@gtmhub/insightboards/models/models";
import { InsightTemplateSuggestionsService } from "@gtmhub/insightboards/services/insight-template-suggestions.service";
import { InsightService, IGetMarketplaceInsightTemplatesQuery, IMarketplaceInsightTemplate, IMarketplaceInsightTemplateInstallRequest } from "@gtmhub/insights";
import { IdMap } from "@gtmhub/util";
import { IController, IComponentOptions, IPromise, IScope } from "angular";

export class InsightTemplateSuggestionsCtrl implements IController {
  insightboard: IInsightboard;
  settings: IInsightTemplateSuggestionSetting;
  indicators: {
    loading?: IIndicator;
    installing?: IdMap<IIndicator>;
  };
  insightTemplates: IMarketplaceInsightTemplate[];
  dashboardId: string;

  static $inject = ["$scope", "InsightService", "InsightTemplateSuggestionsService", "AnalyticsService"];

  constructor(
    private $scope: IScope,
    private insightService: InsightService,
    private insightTemplateSuggestionsService: InsightTemplateSuggestionsService,
    private analyticsService: AnalyticsService
  ) {}
  $onInit() {
    this.dashboardId = this.insightboard.id;
    this.indicators = {
      loading: { progress: true },
      installing: {},
    };

    this.getInsightTemplateSuggestions(this.settings)
      .then((templates: IMarketplaceInsightTemplate[]) => {
        this.insightTemplates = templates;
        delete this.indicators.loading;
      })
      .catch((error) => {
        delete this.indicators.loading;
        console.error(error);
        this.$scope.$emit("insightTemplateSuggestionDismissed", {
          numberOfSuggestionsToShow: 0,
          linkToMarketplaceDismissed: true,
        });
      });
  }

  public dismissSuggestionCard = (templateId: string) => {
    this.insightTemplates = this.insightTemplates.filter((template) => template.id !== templateId);
    const settings = this.settings;
    if (settings.numberOfSuggestionsToShow > 0) {
      settings.numberOfSuggestionsToShow = settings.numberOfSuggestionsToShow - 1;
      this.saveSettings(settings);
    }
  };

  public installInsightTemplate = (templateId: string): void => {
    this.indicators.installing[templateId] = { progress: true };

    const installInsightReq: IMarketplaceInsightTemplateInstallRequest = {
      insightTemplateId: templateId,
    };
    this.insightService
      .installMarketplaceInsightTemplates([installInsightReq])
      .then((res) => {
        const installedInsightIds = res.installedInsightsIds.join(",");

        this.insightService.getInsightsSafe(installedInsightIds).then((insights) => {
          const insight = insights.items[0];

          insight.size = {
            height: 5,
            width: 2,
          };
          this.trackRecommendationHelpfulness({ isUseful: true });
          this.$scope.$emit("insightTemplateSuggestionInstalled", insight);
          this.dismissSuggestionCard(templateId);
          delete this.indicators.installing[templateId];
        });
      })
      .catch((error) => (this.indicators.installing[templateId] = { error }));
  };

  public dismissLinkToMarketplace = () => {
    this.settings.linkToMarketplaceDismissed = true;
    this.saveSettings(this.settings);
  };

  public resetIndicator = (templateId: string): void => {
    delete this.indicators.installing[templateId];
  };

  private trackRecommendationHelpfulness(args: IRecommendationArgs): void {
    //Tracking Insight creation recommendation helpfulness when insights board is empty.
    //Recommendation is helpful if user selects recommended insight
    this.analyticsService.track(recommendation, {
      is_useful: args.isUseful,
      flavor: Flavor.PASSIVE,
      action: "creates_insight",
    });
  }
  // By default we show 2 insight template suggestions on a board
  // If the current insightboard already contains some of the templates from those above, we filter them out
  // User can also toggle off the suggestion card and remove the number of suggestions present for a single insightboard
  private getInsightTemplateSuggestions = (settings: IInsightTemplateSuggestionSetting): IPromise<IMarketplaceInsightTemplate[] | void> => {
    if (settings.numberOfSuggestionsToShow == 0) {
      return Promise.resolve([]);
    }

    const currentInsights = this.insightboard.insights;
    const queryParams: IGetMarketplaceInsightTemplatesQuery = {
      take: 20,
      skip: 0,
      orderBy: "installsCount",
      fields: "id,title,description,requiredEntities{connector},installsCount",
    };

    return this.insightService.getMarketplaceInsightTemplates(queryParams).then((templateResponse) => {
      const templates = templateResponse.items;
      const alreadyInstalledInsightTemplates = currentInsights.filter((insight) => insight.insightSource && insight.insightSource.sourceApplication === "marketplace");

      if (alreadyInstalledInsightTemplates.length == 0) {
        return templates.sort(() => 0.5 - Math.random()).splice(0, settings.numberOfSuggestionsToShow);
      }

      const nonInstalledTemplates: IMarketplaceInsightTemplate[] = [];
      const marketplaceInsightIds = alreadyInstalledInsightTemplates.map((insight) => insight.insightId).join(",");
      const insightFields = "id,title";

      return this.insightService.getInsightsSafe(marketplaceInsightIds, insightFields).then((insightsResponse) => {
        // template exists if the title matches with a marketplace insight template title
        const { items: installedTemplates } = insightsResponse;
        const installedTemplatesMap = {};
        installedTemplates.forEach((template) => {
          installedTemplatesMap[template.title] = template;
        });
        templates.forEach((template) => {
          const isTemplatePresent = installedTemplatesMap[template.title];
          if (isTemplatePresent === undefined) {
            nonInstalledTemplates.push(template);
          }
        });
        return nonInstalledTemplates.sort(() => 0.5 - Math.random()).splice(0, settings.numberOfSuggestionsToShow);
      });
    });
  };

  private saveSettings = (currentSettings: IInsightTemplateSuggestionSetting): void => {
    this.$scope.$emit("insightTemplateSuggestionDismissed", currentSettings);
    this.insightTemplateSuggestionsService.updateSettings(currentSettings);
  };
}

export const InsightTemplateSuggestionsComponent: IComponentOptions = {
  controller: InsightTemplateSuggestionsCtrl,
  templateUrl: require("./insight-template-suggestions.jade"),
  bindings: {
    insightboard: "<",
    settings: "<",
  },
};
